import type { Player } from "../../engine/player";
import type { VendingMachine } from "../../engine/things/vendingMachine";
import type { StockEntry } from "../../engine/vending/stockEntry";
import { getSellInfo } from "../../engine/vending/sellCatalog";
import { nextSellStep, prevSellStep } from "../../engine/vending/sellStepper";
import { getWeaponClass, getWeaponClassRegistryKeys } from "../../engine/serialization";
import { Strings } from "../../engine/strings/strings";
import { Medikit } from "../../engine/items/medikit";
import { Key } from "../../engine/items/key";
import { getPickupIconFilenameByKey } from "../pickupIconMap";

export interface BuyTile {
	/** Index into machine.offers. */
	entryId: string;
	displayName: string;
	iconFilename: string | null;
	packSize: number;
	/** May be fractional — renderer shows totalPrice. */
	unitPrice: number;
	totalPrice: number;
	stockLeft: number;
	isSoldOut: boolean;
	/** Also true for sold-out, to keep the tile disabled. */
	isUnaffordable: boolean;
}

export interface SellRow {
	itemKey: string;
	displayName: string;
	iconFilename: string | null;
	/** weapon.ammo for ammo rows, item.count for inventory rows. */
	availableCount: number;
	basketCount: number;
	pricePerUnit: number;
	packSize: number;
	canStepUp: boolean;
	canStepDown: boolean;
}

export interface SellSummary {
	totalUnits: number;
	totalGold: number;
	canConfirm: boolean;
}

export interface BuyResult {
	success: boolean;
	goldSpent: number;
}

/** Everything not in this set is treated as weapon ammo. */
const INVENTORY_ITEM_KEYS = new Set([Medikit.key, Key.key]);

/** Pure — player + machine in, plain rows out. Basket is renderer-owned; step methods return a NEW Map. commitSale/commitBuy are the intentional mutations. */
export class VendingViewModel {
	private readonly player: Player;
	private readonly machine: VendingMachine;

	constructor(player: Player, machine: VendingMachine) {
		this.player = player;
		this.machine = machine;
	}

	computeBuyTiles(): BuyTile[] {
		return this.machine.offers.map((entry, index) => this.buildBuyTile(entry, index));
	}

	private buildBuyTile(entry: StockEntry, index: number): BuyTile {
		const isSoldOut = entry.stockLeft <= 0;
		const isUnaffordable = this.player.gold < entry.price;
		return {
			entryId: String(index),
			displayName: this.resolveItemName(entry.itemKey),
			iconFilename: getPickupIconFilenameByKey(entry.itemKey),
			packSize: entry.packSize,
			unitPrice: entry.packSize > 0 ? entry.price / entry.packSize : entry.price,
			totalPrice: entry.price,
			stockLeft: entry.stockLeft,
			isSoldOut,
			isUnaffordable,
		};
	}

	computeSellRows(basket: Map<string, number>): SellRow[] {
		const rows: SellRow[] = [];

		for (const weapon of this.player.arsenal.getAllWeapons()) {
			if (weapon.infinite) continue;
			const weaponKey = (weapon.constructor as any).key as string;
			const info = getSellInfo(weaponKey);
			if (!info) continue;
			if (weapon.ammo <= 0) continue;

			rows.push(this.buildSellRow(weaponKey, weapon.ammo, info.unitPrice, info.sellPackSize, basket));
		}

		for (const item of this.player.inventory.getAllItems()) {
			const itemKey = (item.constructor as any).key as string;
			const info = getSellInfo(itemKey);
			if (!info) continue;
			if (item.count <= 0) continue;

			rows.push(this.buildSellRow(itemKey, item.count, info.unitPrice, info.sellPackSize, basket));
		}

		return rows;
	}

	private buildSellRow(
		itemKey: string,
		availableCount: number,
		pricePerUnit: number,
		packSize: number,
		basket: Map<string, number>,
	): SellRow {
		const rawBasket = basket.get(itemKey) ?? 0;
		// Clamp stale basket entries (e.g., player spent ammo mid-turn) so they don't roll up a bogus count.
		const basketCount = Math.max(0, Math.min(rawBasket, availableCount));
		return {
			itemKey,
			displayName: this.resolveItemName(itemKey),
			iconFilename: getPickupIconFilenameByKey(itemKey),
			availableCount,
			basketCount,
			pricePerUnit,
			packSize,
			canStepUp: basketCount < availableCount,
			canStepDown: basketCount > 0,
		};
	}

	computeSellSummary(basket: Map<string, number>): SellSummary {
		const rows = this.computeSellRows(basket);
		let totalUnits = 0;
		let totalGold = 0;
		for (const row of rows) {
			totalUnits += row.basketCount;
			totalGold += row.basketCount * row.pricePerUnit;
		}
		return {
			totalUnits,
			totalGold,
			canConfirm: totalUnits > 0 && this.validateBasket(basket) === null,
		};
	}

	basketStepUp(basket: Map<string, number>, itemKey: string): Map<string, number> {
		const row = this.findRow(itemKey);
		if (!row) return new Map(basket);
		const current = basket.get(itemKey) ?? 0;
		const next = nextSellStep(current, row.packSize, row.availableCount);
		const result = new Map(basket);
		result.set(itemKey, next);
		return result;
	}

	basketStepDown(basket: Map<string, number>, itemKey: string): Map<string, number> {
		const row = this.findRow(itemKey);
		if (!row) return new Map(basket);
		const current = basket.get(itemKey) ?? 0;
		const next = prevSellStep(current, row.packSize, row.availableCount);
		const result = new Map(basket);
		if (next === 0) {
			result.delete(itemKey);
		} else {
			result.set(itemKey, next);
		}
		return result;
	}

	basketClear(): Map<string, number> {
		return new Map();
	}

	/** Returns null when spendable, or a short reason string otherwise. Guards against stale baskets. */
	validateBasket(basket: Map<string, number>): string | null {
		for (const [itemKey, count] of basket) {
			if (count < 0) return `Negative count for ${itemKey}`;
			if (count === 0) continue;
			const available = this.getAvailableCount(itemKey);
			if (available === null) return `Unknown sellable item ${itemKey}`;
			if (count > available) return `Basket has ${count} ${itemKey} but only ${available} available`;
		}
		return null;
	}

	/** No-ops on invalid basket — callers should check canConfirm first. Returns gold awarded. */
	commitSale(basket: Map<string, number>): number {
		if (this.validateBasket(basket) !== null) return 0;

		let totalGold = 0;
		for (const [itemKey, count] of basket) {
			if (count <= 0) continue;
			const info = getSellInfo(itemKey);
			if (!info) continue;

			if (this.isWeaponAmmo(itemKey)) {
				const weapon = this.player.arsenal.getAllWeapons()
					.find((w) => (w.constructor as any).key === itemKey);
				if (weapon && !weapon.infinite) {
					weapon.ammo -= count;
				}
			} else {
				const itemClass = this.getItemClassByKey(itemKey);
				if (itemClass) {
					for (let i = 0; i < count; i++) {
						this.player.inventory.consumeOne(itemClass);
					}
				}
			}
			totalGold += count * info.unitPrice;
		}

		this.player.gold += totalGold;
		return totalGold;
	}

	commitBuy(entryId: string): BuyResult {
		const index = Number(entryId);
		if (!Number.isFinite(index) || index < 0 || index >= this.machine.offers.length) {
			return { success: false, goldSpent: 0 };
		}
		const entry = this.machine.offers[index];
		if (entry.stockLeft <= 0 || this.player.gold < entry.price) {
			return { success: false, goldSpent: 0 };
		}

		this.player.gold -= entry.price;
		entry.stockLeft--;

		if (this.isWeaponAmmo(entry.itemKey)) {
			this.addWeaponAmmo(entry.itemKey, entry.packSize);
		} else {
			this.addInventoryItem(entry.itemKey, entry.packSize);
		}

		return { success: true, goldSpent: entry.price };
	}

	/** Transient row for basketStepUp/Down — avoids recomputing all rows. */
	private findRow(itemKey: string): SellRow | null {
		const available = this.getAvailableCount(itemKey);
		if (available === null) return null;
		const info = getSellInfo(itemKey);
		if (!info) return null;
		return {
			itemKey,
			displayName: this.resolveItemName(itemKey),
			iconFilename: getPickupIconFilenameByKey(itemKey),
			availableCount: available,
			basketCount: 0,
			pricePerUnit: info.unitPrice,
			packSize: info.sellPackSize,
			canStepUp: true,
			canStepDown: false,
		};
	}

	private getAvailableCount(itemKey: string): number | null {
		if (this.isWeaponAmmo(itemKey)) {
			const weapon = this.player.arsenal.getAllWeapons()
				.find((w) => (w.constructor as any).key === itemKey);
			if (!weapon || weapon.infinite) return null;
			return weapon.ammo;
		}
		const item = this.player.inventory.getItemByKey(itemKey);
		return item ? item.count : null;
	}

	private isWeaponAmmo(itemKey: string): boolean {
		if (INVENTORY_ITEM_KEYS.has(itemKey)) return false;
		return getWeaponClassRegistryKeys().includes(itemKey);
	}

	private resolveItemName(itemKey: string): string {
		const prefix = this.isWeaponAmmo(itemKey) ? "wname_" : "iname_";
		return Strings.get(prefix + itemKey) ?? itemKey;
	}

	private getItemClassByKey(itemKey: string): (new (...args: any[]) => any) | null {
		if (itemKey === Medikit.key) return Medikit;
		if (itemKey === Key.key) return Key;
		return null;
	}

	private addWeaponAmmo(weaponKey: string, amount: number): void {
		const cls = getWeaponClass(weaponKey);
		const existing = this.player.arsenal.getAllWeapons().find((w) => w instanceof cls);
		if (existing) {
			if (!existing.infinite) existing.ammo += amount;
		} else {
			const weapon = new cls(this.player);
			weapon.ammo = amount;
			this.player.addWeaponToArsenal(weapon);
		}
	}

	private addInventoryItem(itemKey: string, count: number): void {
		if (itemKey === Medikit.key) {
			this.player.inventory.addItem(new Medikit(count));
		} else if (itemKey === Key.key) {
			this.player.inventory.addItem(new Key(count));
		}
	}
}
